<script setup lang="ts">
import { computed } from 'vue'
import { PAGINATION_DEFAULTS } from '@/constants/ui'

const props = withDefaults(defineProps<{
  total: number
  pageSize?: number
  currentPage?: number
  pageSizes?: number[]
  showTotal?: boolean
  showPageSize?: boolean
  maxPages?: number
}>(), {
  pageSize: PAGINATION_DEFAULTS.PAGE_SIZE,
  currentPage: PAGINATION_DEFAULTS.CURRENT_PAGE,
  pageSizes: () => [...PAGINATION_DEFAULTS.PAGE_SIZES],
  showTotal: true,
  showPageSize: true,
  maxPages: PAGINATION_DEFAULTS.MAX_PAGES
})

const emit = defineEmits<{
  'update:currentPage': [page: number]
  'update:pageSize': [size: number]
  'change': [page: number, size: number]
}>()

const totalPages = computed(() => Math.ceil(props.total / props.pageSize) || 1)

const startItem = computed(() => {
  return props.total === 0 ? 0 : (props.currentPage - 1) * props.pageSize + 1
})

const endItem = computed(() => {
  return Math.min(props.currentPage * props.pageSize, props.total)
})

const pages = computed(() => {
  const pages: (number | string)[] = []
  const current = props.currentPage
  const total = totalPages.value
  const max = props.maxPages

  if (total <= max + 2) {
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    pages.push(1)
    
    let start = Math.max(2, current - Math.floor(max / 2))
    let end = Math.min(total - 1, start + max - 1)
    
    if (end - start < max - 1) {
      start = Math.max(2, end - max + 1)
    }
    
    if (start > 2) {
      pages.push('...')
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
    
    if (end < total - 1) {
      pages.push('...')
    }
    
    pages.push(total)
  }
  
  return pages
})

function goToPage(page: number): void {
  if (page < 1 || page > totalPages.value || page === props.currentPage) return
  emit('update:currentPage', page)
  emit('change', page, props.pageSize)
}

function prevPage(): void {
  goToPage(props.currentPage - 1)
}

function nextPage(): void {
  goToPage(props.currentPage + 1)
}

function handlePageSizeChange(event: Event): void {
  const target = event.target as HTMLSelectElement
  const newSize = parseInt(target.value, 10)
  emit('update:pageSize', newSize)
  emit('update:currentPage', 1)
  emit('change', 1, newSize)
}
</script>

<template>
  <div class="base-pagination">
    <div class="base-pagination__left">
      <div v-if="showTotal" class="base-pagination__total">
        共 <span>{{ total }}</span> 条
      </div>

      <div v-if="showPageSize" class="base-pagination__sizes">
        <select
          class="base-pagination__select"
          :value="pageSize"
          @change="handlePageSizeChange"
        >
          <option v-for="size in pageSizes" :key="size" :value="size">
            {{ size }} 条/页
          </option>
        </select>
      </div>
    </div>

    <div class="base-pagination__pages">
      <button
        type="button"
        class="base-pagination__btn base-pagination__btn--nav"
        :disabled="currentPage <= 1"
        @click="prevPage"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      
      <template v-for="page in pages" :key="page">
        <button
          v-if="page !== '...'"
          type="button"
          class="base-pagination__btn"
          :class="{ 'base-pagination__btn--active': page === currentPage }"
          @click="goToPage(page as number)"
        >
          {{ page }}
        </button>
        <span v-else class="base-pagination__ellipsis">...</span>
      </template>
      
      <button
        type="button"
        class="base-pagination__btn base-pagination__btn--nav"
        :disabled="currentPage >= totalPages"
        @click="nextPage"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
    </div>

    <div class="base-pagination__right">
      <div class="base-pagination__info">
        {{ startItem }}-{{ endItem }} / {{ total }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.base-pagination {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  column-gap: var(--spacing-4);
  row-gap: var(--spacing-3);
  padding: var(--spacing-4);
  background: var(--surface-dim);
  border-top: 1px solid var(--border-color);

  &__left {
    display: flex;
    align-items: center;
    gap: var(--spacing-4);
    min-width: 0;
  }

  &__right {
    display: flex;
    justify-content: flex-end;
    min-width: 0;
  }

  &__total {
    white-space: nowrap;
    font-size: var(--text-13);
    color: var(--text-secondary);

    span {
      font-weight: var(--font-weight-medium);
      color: var(--text-primary);
    }
  }

  &__sizes {
    display: flex;
    align-items: center;
    white-space: nowrap;
  }

  &__select {
    padding: var(--spacing-1-5) var(--spacing-7) var(--spacing-1-5) var(--spacing-2-5);
    background: var(--white);
    border: 1px solid var(--border-hover);
    border-radius: var(--radius);
    font-size: var(--text-13);
    color: var(--text-primary);
    cursor: pointer;
    outline: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 8px center;

    &:hover {
      border-color: var(--primary);
    }

    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 2px var(--blue-alpha-15);
    }
  }

  &__pages {
    display: flex;
    align-items: center;
    gap: var(--spacing-1);
    justify-self: center;
  }

  &__btn {
    min-width: 32px;
    height: 32px;
    padding: 0 var(--spacing-2);
    border: 1px solid var(--border-hover);
    background: var(--white);
    border-radius: var(--radius);
    font-size: var(--text-13);
    color: var(--text-primary);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all var(--transition-fast);

    &:hover:not(:disabled) {
      border-color: var(--primary);
      color: var(--primary);
      background: var(--primary-50);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--active {
      background: var(--primary);
      border-color: var(--primary);
      color: var(--white);

      &:hover:not(:disabled) {
        background: var(--primary-dark);
        border-color: var(--primary-dark);
        color: var(--white);
      }
    }

    &--nav {
      padding: 0;
    }
  }

  &__ellipsis {
    min-width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-secondary);
  }

  &__info {
    white-space: nowrap;
    font-size: var(--text-13);
    color: var(--text-secondary);
  }
}

@media (max-width: 900px) {
  .base-pagination {
    grid-template-columns: 1fr;
  }

  .base-pagination__left,
  .base-pagination__pages,
  .base-pagination__right {
    justify-content: center;
  }
}
</style>
